import { useEffect, useRef, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StepProgress } from "../components/StepProgress";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  UploadCloud,
  FileText,
  FileSpreadsheet,
  Presentation,
  X,
  Info,
  Lock,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { rfpStoreSet, rfpStoreRemove } from "../lib/rfpApi";

const RFP_STEPS = ["Client Information", "Document Upload", "Review & Submit"];

const MAX_FILES = 10;
const RFP_FILES_STORAGE_KEY = "primes4_rfp_files";

const ACCEPTED_EXTENSIONS = ["pdf", "docx", "pptx", "xlsx"];
const ACCEPT_ATTR =
  ".pdf,.docx,.pptx,.xlsx," +
  "application/pdf," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.openxmlformats-officedocument.presentationml.presentation," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function fileExt(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

function isAccepted(file) {
  return ACCEPTED_EXTENSIONS.includes(fileExt(file.name));
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

function iconForExt(ext) {
  if (ext === "xlsx") return FileSpreadsheet;
  if (ext === "pptx") return Presentation;
  return FileText;
}

export default function EvaluateRfpIntakePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]); // { id, file, name, ext, size }
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const persistMetadata = (list) => {
    try {
      sessionStorage.setItem(
        RFP_FILES_STORAGE_KEY,
        JSON.stringify(
          list.map((f) => ({ id: f.id, name: f.name, ext: f.ext, size: f.size }))
        )
      );
    } catch (_e) {
      /* ignore */
    }
  };

  // Restore prior session metadata so the file list count is preserved across reloads
  // (actual File blobs aren't restored — those require a real backend upload step).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(RFP_FILES_STORAGE_KEY);
      if (!raw) return;
      const meta = JSON.parse(raw);
      if (Array.isArray(meta) && meta.length > 0) {
        setFiles(
          meta.map((m, idx) => ({
            id: m.id || `restored-${idx}-${m.name}`,
            file: null,
            name: m.name,
            ext: m.ext,
            size: m.size,
            restored: true,
          }))
        );
      }
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const addFiles = (incoming) => {
    if (!incoming || incoming.length === 0) return;
    setError("");

    const existingNames = new Set(files.map((f) => f.name));
    const accepted = [];
    let hasInvalidType = false;

    for (const file of incoming) {
      if (!isAccepted(file)) {
        hasInvalidType = true;
        continue;
      }
      if (existingNames.has(file.name)) continue;
      existingNames.add(file.name);
      const fileId = `${file.name}-${file.size}-${file.lastModified || ""}`;
      rfpStoreSet(fileId, file);
      accepted.push({
        id: fileId,
        file,
        name: file.name,
        ext: fileExt(file.name),
        size: file.size,
        restored: false,
      });
    }

    if (hasInvalidType) {
      setError(
        "Unsupported file type. Please upload PDF, DOCX, PPTX, or XLSX files."
      );
    }

    if (accepted.length === 0) return;

    const combined = [...files, ...accepted];
    if (combined.length > MAX_FILES) {
      setError("You can upload up to 10 documents.");
      const truncated = combined.slice(0, MAX_FILES);
      setFiles(truncated);
      persistMetadata(truncated);
      return;
    }
    setFiles(combined);
    persistMetadata(combined);
  };

  const handleSelectClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleInputChange = (e) => {
    addFiles(Array.from(e.target.files || []));
    // Reset input so selecting the same file again still fires onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (id) => {
    rfpStoreRemove(id);
    const next = files.filter((f) => f.id !== id);
    setFiles(next);
    persistMetadata(next);
    setError("");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handleContinue = () => {
    if (files.length === 0) {
      setError("Please upload at least one RFP or requirements document.");
      return;
    }
    persistMetadata(files);
    navigate("/evaluate/rfp-review");
  };

  return (
    <div
      data-testid="evaluate-rfp-intake-page"
      className="min-h-screen flex flex-col bg-[#F8FAFC]"
    >
      <Header />

      <StepProgress currentStep={2} total={3} labels={RFP_STEPS} />

      <main className="flex-1 pt-8 sm:pt-10 pb-12">
        <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page header */}
          <div className="mb-7">
            <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5 mb-2">
              <Sparkles
                className="w-3.5 h-3.5 text-[#025979]"
                strokeWidth={1.5}
              />
              <span className="text-xs font-medium text-[#025979]">
                Step 2 — Document Upload
              </span>
            </div>
            <p
              data-testid="rfp-intake-step-indicator"
              className="text-[11px] text-[#9CA3AF] mb-4 tracking-wide"
            >
              Step 2 of 3 • Upload your RFP &amp; requirement documents
            </p>
            <h1
              data-testid="rfp-intake-title"
              className="text-xl sm:text-2xl font-bold tracking-tight text-[#1F2937]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              RFP &amp; Requirements Analysis
            </h1>
            <p
              data-testid="rfp-intake-subtitle"
              className="mt-2 text-[13px] text-[#4B5563] leading-relaxed max-w-2xl"
            >
              Upload RFP, scope, process, integration, or requirements documents
              for SmartBid analysis.
            </p>
          </div>

          {/* Card */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">
            <div className="p-6 sm:p-8">
              {/* Upload zone */}
              <div
                data-testid="rfp-intake-upload-zone"
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleSelectClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectClick();
                  }
                }}
                className={[
                  "border-2 border-dashed rounded-sm px-6 py-10 sm:py-12 flex flex-col items-center text-center transition-colors cursor-pointer",
                  isDragging
                    ? "border-[#025979] bg-[#E0F2FE]/40"
                    : "border-[#CBD5E1] bg-[#F8FAFC] hover:border-[#025979]/60",
                ].join(" ")}
              >
                <div className="w-14 h-14 rounded-sm bg-[#F3E8FF] flex items-center justify-center mb-4">
                  <UploadCloud
                    className="w-7 h-7 text-[#6D28D9]"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="text-sm font-medium text-[#111827]">
                  Drag &amp; drop your RFP documents here
                </p>
                <p className="mt-1 text-xs text-[#6B7280]">
                  or click to browse files
                </p>

                <Button
                  data-testid="rfp-intake-select-btn"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectClick();
                  }}
                  className="mt-5 bg-[#025979] hover:bg-[#03738C] text-white px-6 py-2 text-sm font-medium rounded-sm"
                >
                  <UploadCloud className="mr-2 w-4 h-4" />
                  Select Files
                </Button>

                <input
                  ref={fileInputRef}
                  data-testid="rfp-intake-file-input"
                  type="file"
                  multiple
                  accept={ACCEPT_ATTR}
                  className="hidden"
                  onChange={handleInputChange}
                />

                {/* Supported file types */}
                <div
                  data-testid="rfp-intake-supported-types"
                  className="mt-5 text-[11px] text-[#6B7280] tracking-wide"
                >
                  PDF · DOCX · PPTX · XLSX
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div
                  data-testid="rfp-intake-error"
                  className="mt-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-sm px-3.5 py-2.5"
                >
                  <AlertCircle
                    className="w-4 h-4 text-red-600 mt-0.5 shrink-0"
                    strokeWidth={1.75}
                  />
                  <span className="text-xs text-red-700 leading-relaxed">
                    {error}
                  </span>
                </div>
              )}

              {/* Selected files list */}
              {files.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3
                      className="text-sm font-semibold text-[#1F2937] uppercase tracking-wider"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                    >
                      Selected Documents
                    </h3>
                    <span
                      data-testid="rfp-intake-file-count"
                      className="text-[11px] font-medium text-[#6B7280]"
                    >
                      {files.length} of {MAX_FILES}
                    </span>
                  </div>
                  <ul
                    data-testid="rfp-intake-file-list"
                    className="divide-y divide-[#E5E7EB] border border-[#E5E7EB] rounded-sm bg-white"
                  >
                    {files.map((f) => {
                      const Icon = iconForExt(f.ext);
                      return (
                        <li
                          key={f.id}
                          data-testid={`rfp-intake-file-item-${f.name}`}
                          className="flex items-center gap-3 px-4 py-3"
                        >
                          <div className="w-9 h-9 rounded-sm bg-[#E0F2FE] flex items-center justify-center shrink-0">
                            <Icon
                              className="w-4 h-4 text-[#025979]"
                              strokeWidth={1.5}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#1F2937] truncate">
                              {f.name}
                            </div>
                            <div className="text-[11px] text-[#6B7280] mt-0.5">
                              {(f.ext || "FILE").toUpperCase()} ·{" "}
                              {formatBytes(f.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            data-testid={`rfp-intake-remove-${f.name}`}
                            onClick={() => removeFile(f.id)}
                            aria-label={`Remove ${f.name}`}
                            className="p-1.5 rounded-sm text-[#6B7280] hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <X className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Info messages */}
            <div className="p-6 sm:p-8 space-y-3">
              <div
                data-testid="rfp-intake-priva-info"
                className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3"
              >
                <Info
                  className="w-4 h-4 text-[#6D28D9] mt-0.5 shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  PRIVA will auto-extract RFP requirements, warehouse scope,
                  integrations, assumptions, risks, KPIs, and bid inputs from
                  your uploaded documents.
                </span>
              </div>

              <div
                data-testid="rfp-intake-privacy-info"
                className="flex items-start gap-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3"
              >
                <Lock
                  className="w-4 h-4 text-[#025979] mt-0.5 shrink-0"
                  strokeWidth={1.5}
                />
                <span className="text-sm text-[#4B5563] leading-relaxed">
                  Your uploaded documents are handled securely and are only
                  available inside the authorized SmartBid workflow.
                </span>
              </div>
            </div>

            <div className="border-t border-[#E5E7EB]"></div>

            {/* Footer actions */}
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-3">
              <Button
                data-testid="rfp-intake-back-btn"
                variant="outline"
                onClick={() => navigate("/evaluate/rfp-client-info")}
                className="rounded-sm h-11 px-6 text-sm font-medium border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors"
              >
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to Client Information
              </Button>

              <Button
                data-testid="rfp-intake-continue-btn"
                onClick={handleContinue}
                className="bg-[#025979] hover:bg-[#03738C] text-white h-11 px-8 text-sm font-semibold rounded-sm transition-colors shadow-sm hover:shadow"
              >
                Continue to Review
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
